import {
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Chart as ChartJS,
  ChartData,
} from "chart.js";
import classNames from "classnames";
import dayjs from "dayjs";
import { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import { TypedChartComponent } from "react-chartjs-2/dist/types";
import AscHeading from "../AscTypography/AscHeading/AscHeading";
import styles from "./AscAverageWeeklyTimeSpentChart.module.scss";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip);

export interface AscAverageWeeklyTimeSpentChartProps {
  className?: string;
  data: {
    dayOfWeek: number;
    globalDurationInSeconds: number;
    usersDurationInSeconds: number;
    schoolDurationInSeconds: number;
  }[];
  ownAverageLabel?: string;
  ownAverage: number;
  globalAverageLabel?: string;
  globalAverage: number;
  schoolAverageLabel?: string;
  schoolAverage: number;
  title?: string;
  loading?: boolean;
}

export default function AscAverageWeeklyTimeSpentChart({
  className,
  data: _dataProp,
  ownAverageLabel = "Own Average",
  ownAverage,
  globalAverageLabel = "Global Average",
  globalAverage,
  schoolAverageLabel = "School Average",
  schoolAverage,
  title = "Weekly average time spent skilling up",
  loading = false,
}: AscAverageWeeklyTimeSpentChartProps) {
  const data: ChartData<"bar", number[], string> = useMemo(
    () => ({
      labels: ["S", "M", "T", "W", "T", "F", "S"],
      datasets: [
        {
          label: ownAverageLabel,
          data: _dataProp?.map((d) => d.usersDurationInSeconds / 3600) ?? [],
          backgroundColor: "#4239FB",
        },
        {
          label: globalAverageLabel,
          data: _dataProp?.map((d) => d.globalDurationInSeconds / 3600) ?? [],
          backgroundColor: "#64C181",
        },
        {
          label: schoolAverageLabel,
          data: _dataProp?.map((d) => d.schoolDurationInSeconds / 3600) ?? [],
          backgroundColor: "#FF9B04",
        },
      ],
    }),
    [_dataProp, globalAverageLabel, ownAverageLabel, schoolAverageLabel]
  );
  const averageTimeSpent = useMemo(() => {
    const globalAverageFormatted = dayjs
      .duration(globalAverage, "seconds")
      .format("H[h] m[m]");

    const ownAverageFormatted = dayjs
      .duration(ownAverage, "seconds")
      .format("H[h] m[m]");

    const schoolAverageFormatted = dayjs
      .duration(schoolAverage, "seconds")
      .format("H[h] m[m]");

    return {
      globalAverage: globalAverageFormatted,
      ownAverage: ownAverageFormatted,
      schoolAverage: schoolAverageFormatted,
    };
  }, [schoolAverage, ownAverage, globalAverage]);

  const options: Parameters<TypedChartComponent<"bar">>[0]["options"] = useMemo(
    () => ({
      type: "bar",
      responsive: true,
      aspectRatio: 1, // Set the aspect ratio (width / height)
      plugins: {
        tooltip: {
          bodyColor: "#0B072A",
          backgroundColor: "#ffffff",
          title: () => "",
          titleFont: {
            size: 0,
          },
          bodyFont: {
            family: "Poppins",
            size: 14,
            weight: "bolder",
          },
          displayColors: false,
          callbacks: {
            label: function (context) {
              const value = context.parsed.y || 0;
              return `${Math.floor(value)}h ${Math.round(
                (value - Math.floor(value)) * 60
              )}m `;
            },
          },
        },
      },
      scales: {
        x: {
          border: {
            dash: [4, 4],
          },
          grid: {
            drawTicks: false,
          },
          ticks: {
            color: "#9499B5",
            font: {
              family: "Poppins",
              size: 12,
              weight: "bolder",
              lineHeight: 4,
            },
          },
        },
        y: {
          position: "right",
          border: {
            dash: [4, 4],
          },
          grid: {
            drawTicks: false,
          },
          ticks: {
            // Include a percentage sign in the ticks
            callback: function (value) {
              if (
                (typeof value === "string" && value?.includes(".")) ||
                (typeof value === "number" && value % 1 !== 0)
              ) {
                return "";
              }
              return `${value}h`;
            },
            color: "#9499B5",
            font: {
              family: "Poppins",
              size: 12,
              weight: "bolder",
              lineHeight: 4,
            },
          },
        },
      },
      size: {
        width: "100%",
      },
    }),
    []
  );
  return (
    <div
      data-testid="asc-average-weekly-time-spent-chart"
      className={classNames(
        styles.ground,
        {
          [styles.loading]: loading,
        },
        className
      )}
    >
      <AscHeading className={styles.title} level={4}>
        {title}
      </AscHeading>
      {data && <Bar options={options} data={data} />}
      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <div
            className={styles.legendItemColor}
            style={{
              background: "#4239FB",
            }}
          />
          <div className={styles.legendTextContainer}>
            <div className={styles.legendItemLabel}>{ownAverageLabel}</div>
            <div className={styles.legendItemValue}>
              <b>{averageTimeSpent.ownAverage}</b> per day
            </div>
          </div>
        </div>
        <div className={styles.legendItem}>
          <div
            className={styles.legendItemColor}
            style={{
              background: "#64C181",
            }}
          />
          <div className={styles.legendTextContainer}>
            <div className={styles.legendItemLabel}>{globalAverageLabel}</div>
            <div className={styles.legendItemValue}>
              <b>{averageTimeSpent.globalAverage}</b> per day
            </div>
          </div>
        </div>
        <div className={styles.legendItem}>
          <div
            className={styles.legendItemColor}
            style={{
              background: "#FF9B04",
            }}
          />
          <div className={styles.legendTextContainer}>
            <div className={styles.legendItemLabel}>{schoolAverageLabel}</div>
            <div className={styles.legendItemValue}>
              <b>{averageTimeSpent.schoolAverage}</b> per day
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
